import { useQuery } from '@tanstack/react-query';
import { CircleAlert } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import {
  GatewayActions,
  GatewayBrand,
  GatewayContent,
  GatewayShell,
  GatewaySpinner,
} from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { orpc } from '@/_shared/lib/orpc';

import type { NativeSignInConfig } from '../../types';

/** A full-page move to the web app, which is not part of this router. */
function GoToWebApp({ url }: { url: string }): React.ReactElement {
  useEffect(() => window.location.replace(url), [url]);
  return <GatewaySpinner mode="fullScreen" />;
}

/** Sign-up and password pages exist only with Lookout's own sign-in; otherwise the path is unknown. */
export function RequireNativeSignIn({
  children,
  webAppLink,
}: {
  children: (config: NativeSignInConfig) => React.ReactNode;
  /**
   * The web app page that finishes the same emailed link, or null. While Lookout's own sign-in is off, for example
   * after a rollback, the link goes on there, so that the user does not lose it.
   */
  webAppLink?: (webAppUrl: string) => string | null;
}): React.ReactElement {
  const { data, isLoading, isFetching, refetch } = useQuery(orpc.auth.providers.queryOptions());

  if (isLoading) return <GatewaySpinner mode="fullScreen" />;
  // A failed request does not mean that sign-in is off. The page stays, so an emailed link keeps its token.
  if (!data) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<CircleAlert />}
          variant="error"
          title="This page did not load"
          description="Check your connection and try again."
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" loading={isFetching} onClick={() => void refetch()}>
            Try again
          </Button>
        </GatewayActions>
      </GatewayShell>
    );
  }
  if (!data.nativeSignIn) {
    const forward = data.webAppUrl && webAppLink ? webAppLink(data.webAppUrl) : null;
    return forward ? <GoToWebApp url={forward} /> : <Navigate to="/" replace />;
  }
  return <>{children(data.nativeSignIn)}</>;
}
